import popupStyles from "../deleteConfirmation/deleteConfirmation.module.css";
import styles from "./permanentDeleteConfirmation.module.css";
import { IPopupWithoutProps } from "../../../types/types";
import { IUserReport } from "../../../services/types/users.types";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { useMemo, useState } from "react";

const PERMANENT_DELETE_CONFIRM_TOKEN = "DELETE";

function splitBulletLines(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function displayUserLabel(user: IUserReport): string {
  const full = user.Users.FullName?.trim();
  if (full) return full;
  const parts = [user.Users.FirstName, user.Users.LastName].filter(Boolean).join(" ").trim();
  return parts || String(user.Users.Id);
}

/** Critical-warning confirmation for super-admin permanent user delete (DML type 4). */
function PermanentDeleteConfirmation({
  onClose,
  onDelete,
  user,
}: IPopupWithoutProps & {
  user: IUserReport;
  onDelete: (options: { deleteClinicalData: boolean }) => void;
}) {
  const [deleteClinicalData, setDeleteClinicalData] = useState(false);
  const [clinicalAck, setClinicalAck] = useState(false);
  const [typedVerify, setTypedVerify] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");

  const verifyExpected = useMemo(() => String(user.Users.Id ?? "").trim(), [user.Users.Id]);
  const userDisplayName = useMemo(
    () => displayUserLabel(user),
    [user.Users.FullName, user.Users.FirstName, user.Users.LastName, user.Users.Id]
  );

  const trimmedDelete = typedConfirm.trim();
  const deleteOk = trimmedDelete === PERMANENT_DELETE_CONFIRM_TOKEN;
  const showDeleteMismatch = trimmedDelete.length > 0 && !deleteOk;

  const verifyOk =
    verifyExpected.length > 0 && typedVerify.trim() === verifyExpected;

  const showVerifyMismatch =
    deleteClinicalData &&
    typedVerify.trim().length > 0 &&
    verifyExpected.length > 0 &&
    !verifyOk;

  const hardSecondStepOk =
    !deleteClinicalData || (clinicalAck && verifyOk && verifyExpected.length > 0);

  const canSubmit = deleteOk && hardSecondStepOk;

  const erasedLines = splitBulletLines(t("PERMANENT_DELETE_LIST_ERASED"));
  const keptSummaryKey = deleteClinicalData
    ? "PERMANENT_DELETE_KEPT_FULL_CLINICAL"
    : "PERMANENT_DELETE_KEPT_SOFT_CLINICAL";
  const keptSectionTitleKey = deleteClinicalData
    ? "PERMANENT_DELETE_SECTION_DATA_LOSS"
    : "PERMANENT_DELETE_SECTION_KEPT";

  return (
    <article
      className={popupStyles.popup}
      data-testid="permanent-delete-confirmation-popup"
    >
      <div className={styles.root}>
        <div className={styles.criticalRibbon} role="alert">
          <span className={styles.warningGlyph} aria-hidden>
            ⚠
          </span>
          <div className={styles.ribbonText}>
            <p className={styles.criticalTitle}>{t("PERMANENT_DELETE_CRITICAL_TITLE")}</p>
            <p className={styles.criticalSubtitle}>{t("PERMANENT_DELETE_CRITICAL_SUBTITLE")}</p>
          </div>
        </div>

        <div className={styles.criticalPanel}>
          <p className={styles.lead}>{t("PERMANENT_DELETE_LEAD")}</p>
          <p className={styles.visibleReminder} role="note">
            {t("PERMANENT_DELETE_VISIBLE_REMINDER")}
          </p>

          <details className={styles.moreDetails}>
            <summary className={styles.moreSummary}>{t("PERMANENT_DELETE_MORE_DETAILS_SUMMARY")}</summary>
            <div className={styles.moreBody}>
              <p className={styles.softVsHard}>{t("PERMANENT_DELETE_VS_SOFT_DELETE")}</p>

              <section className={`${styles.section} ${styles.sectionErase}`} aria-labelledby="pd-heading-erased">
                <h2 id="pd-heading-erased" className={styles.sectionTitle}>
                  {t("PERMANENT_DELETE_SECTION_ERASED")}
                </h2>
                <ul className={styles.bulletList}>
                  {erasedLines.map((line, index) => (
                    <li key={`e-${index}`}>{line}</li>
                  ))}
                </ul>
                <p className={styles.meetingRecordsNote}>{t("PERMANENT_DELETE_MEETING_RECORDS_NOTE")}</p>
              </section>

              <section
                className={`${styles.section} ${styles.sectionKeep} ${deleteClinicalData ? styles.sectionKeep_danger : ""}`}
                aria-labelledby="pd-heading-kept"
              >
                <h2 id="pd-heading-kept" className={styles.sectionTitle}>
                  {t(keptSectionTitleKey)}
                </h2>
                <p
                  className={
                    deleteClinicalData ? `${styles.keptSummary} ${styles.keptSummaryDanger}` : styles.keptSummary
                  }
                >
                  {t(keptSummaryKey)}
                </p>
              </section>
            </div>
          </details>

          <fieldset className={styles.clinicalFieldset}>
            <legend className={styles.clinicalLegend}>{t("PERMANENT_DELETE_CLINICAL_LEGEND")}</legend>
            <div className={styles.clinicalOptions}>
              <label className={styles.clinicalOption}>
                <input
                  type="radio"
                  name="permanent-delete-clinical"
                  checked={!deleteClinicalData}
                  onChange={() => {
                    setDeleteClinicalData(false);
                    setClinicalAck(false);
                    setTypedVerify("");
                  }}
                />
                <span>
                  <span className={styles.clinicalOptionTitle}>
                    {t("PERMANENT_DELETE_CLINICAL_STANDARD_LABEL")}
                  </span>
                  {!deleteClinicalData ? (
                    <span className={styles.clinicalOptionDesc}>{t("PERMANENT_DELETE_CLINICAL_STANDARD_DESC")}</span>
                  ) : null}
                </span>
              </label>
              <label className={styles.clinicalOption}>
                <input
                  type="radio"
                  name="permanent-delete-clinical"
                  checked={deleteClinicalData}
                  onChange={() => {
                    setDeleteClinicalData(true);
                    setClinicalAck(false);
                    setTypedVerify("");
                  }}
                />
                <span>
                  <span className={styles.clinicalOptionTitle}>{t("PERMANENT_DELETE_CLINICAL_FULL_LABEL")}</span>
                  {deleteClinicalData ? (
                    <span className={styles.clinicalOptionDesc}>{t("PERMANENT_DELETE_CLINICAL_FULL_DESC")}</span>
                  ) : null}
                </span>
              </label>
            </div>
          </fieldset>

          {deleteClinicalData ? (
            <div className={styles.hardClinicalPanel} role="region" aria-labelledby="pd-hard-second-title">
              <p id="pd-hard-second-title" className={styles.hardClinicalTitle}>
                {t("PERMANENT_DELETE_HARD_SECOND_STEP_TITLE")}
              </p>
              <p className={styles.hardClinicalWarning}>{t("PERMANENT_DELETE_HARD_CLINICAL_WARNING")}</p>
              <label className={styles.ackLabel}>
                <input
                  type="checkbox"
                  checked={clinicalAck}
                  onChange={(e) => setClinicalAck(e.target.checked)}
                  data-testid="permanent-delete-clinical-ack"
                />
                {t("PERMANENT_DELETE_HARD_CLINICAL_ACK")}
              </label>
              <div className={styles.verifyIdentityBanner} aria-live="polite">
                <div className={styles.verifyIdentityName}>{userDisplayName}</div>
                <div className={styles.verifyIdentityRow}>
                  <span className={styles.verifyIdentityMeta}>{t("PERMANENT_DELETE_VERIFY_ID_CAPTION")}</span>
                  <span className={styles.verifyIdentityId} dir="ltr">
                    {verifyExpected}
                  </span>
                </div>
              </div>
              <label className={styles.verifyLabel} htmlFor="permanent-delete-verify-input">
                {t("PERMANENT_DELETE_VERIFY_ID_LABEL")}
              </label>
              <input
                id="permanent-delete-verify-input"
                type="text"
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                value={typedVerify}
                onChange={(e) => setTypedVerify(e.target.value)}
                placeholder={t("PERMANENT_DELETE_VERIFY_PLACEHOLDER")}
                className={`${styles.verifyInput} ${showVerifyMismatch ? styles.confirmInput_invalid : ""}`}
                aria-invalid={showVerifyMismatch}
                data-testid="permanent-delete-verify-input"
              />
              {showVerifyMismatch ? (
                <p className={styles.confirmMismatch} role="status">
                  {t("PERMANENT_DELETE_VERIFY_MISMATCH")}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className={styles.confirmFooter}>
            <p className={styles.footerQuestion}>{t("PERMANENT_DELETE_QUESTION")}</p>
            <div className={styles.confirmFields}>
              <label className={styles.confirmLabel} htmlFor="permanent-delete-confirm-input">
                {t("PERMANENT_DELETE_TYPE_CONFIRM_LABEL")}
              </label>
              <input
                id="permanent-delete-confirm-input"
                type="text"
                dir="ltr"
                autoComplete="off"
                spellCheck={false}
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                placeholder={t("PERMANENT_DELETE_TYPE_CONFIRM_PLACEHOLDER")}
                className={`${styles.confirmInput} ${showDeleteMismatch ? styles.confirmInput_invalid : ""}`}
                aria-invalid={showDeleteMismatch}
                data-testid="permanent-delete-confirm-input"
              />
              {showDeleteMismatch ? (
                <p className={styles.confirmMismatch} role="status">
                  {t("PERMANENT_DELETE_TYPE_CONFIRM_MISMATCH")}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={`button ${popupStyles.popup__nextBtn} ${styles.btnDanger} hover`}
        data-testid="confirm-permanent-delete-button"
        disabled={!canSubmit}
        onClick={() => {
          onDelete({ deleteClinicalData });
          onClose();
        }}
      >
        {t("CONFIRM")}
      </button>
    </article>
  );
}

export default PermanentDeleteConfirmation;
